/**
 * CallLogsSummary — client-side summary for the Call Logs report.
 *
 * The backend handler returns only `records`, so all aggregates are derived
 * here (utils/callOutcome.js callLogStats; the scheduled report's
 * report_generator.callLogsStats computes the same): total calls, Answer Rate,
 * total + average talk time, and breakdowns by call type and by outcome.
 *
 * Answer Rate covers every call type, unlike the Time to Connect reports, which
 * count intercom calls only (docs/TIME_TO_CONNECT_PLAN.md, phase 4 definition 7).
 */

import { useMemo } from 'react';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';
import { callLogStats } from '../../../../utils/callOutcome.js';

const ANSWER_RATE_INFO =
    'Answered calls as a share of calls that reached the person or device called (answered, no answer or busy). '
    + 'Failed calls, calls not placed, and calls logged before call outcomes were recorded are left out.';
const AVG_TALK_INFO = 'Average talk time of answered calls, from when the call was answered until it ended.';

// seconds → "H:MM:SS" (hours only when > 0), else "M:SS"
function formatSeconds(total) {
    const t = Math.max(0, Math.round(total));
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const s = t % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** A breakdown section: name | bar | count (pct%). */
const Breakdown = ({ title, items, total }) => (
    <div className="pp-section" style={{ marginTop: 'var(--space-4)' }}>
        <div className="pp-section__header">
            <h3 className="pp-section__title">{title}</h3>
        </div>
        <div className="pp-section__body">
            {items.length === 0 ? (
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                    No calls for the selected period.
                </span>
            ) : (
                <ul className="cl-breakdown">
                    {items.map((b) => {
                        const pct = total > 0 ? Math.round((b.count / total) * 100) : 0;
                        return (
                            <li key={b.name} className="cl-breakdown__item">
                                <span className="cl-breakdown__name">{b.name}</span>
                                <span className="cl-breakdown__bar-wrap" aria-hidden="true">
                                    <span className="cl-breakdown__bar" style={{ width: `${pct}%` }} />
                                </span>
                                <span className="cl-breakdown__count">{b.count.toLocaleString()} ({pct}%)</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    </div>
);

const CallLogsSummary = ({ records }) => {
    const stats = useMemo(() => {
        const rows = records || [];
        const byType = new Map();
        rows.forEach((r) => {
            const name = r.call_type_name || 'Unknown';
            byType.set(name, (byType.get(name) || 0) + 1);
        });
        const breakdown = [...byType.entries()]
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count);
        return { ...callLogStats(rows), breakdown };
    }, [records]);

    // Only once outcomes exist in the range; before that every row is "Not recorded".
    const hasOutcomes = stats.byOutcome.some((b) => b.name !== 'Not recorded');

    return (
        <div className="cl-summary">
            {/* Stat cards */}
            <div className="cl-summary__cards">
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{stats.total.toLocaleString()}</div>
                    <div className="ar-summary-card__label">Total Calls</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">
                        {stats.answerRate == null ? '—' : `${stats.answerRate.toFixed(1)}%`}
                    </div>
                    <div className="ar-summary-card__label">
                        Answer Rate <HeaderInfoTip title="Answer Rate" body={ANSWER_RATE_INFO} />
                    </div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{formatSeconds(stats.totalTalkSeconds)}</div>
                    <div className="ar-summary-card__label">Total Talk Time</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">
                        {stats.avgTalkSeconds == null ? '—' : formatSeconds(stats.avgTalkSeconds)}
                    </div>
                    <div className="ar-summary-card__label">
                        Avg Talk Time <HeaderInfoTip title="Avg Talk Time" body={AVG_TALK_INFO} />
                    </div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{stats.breakdown.length.toLocaleString()}</div>
                    <div className="ar-summary-card__label">Call Types</div>
                </div>
            </div>

            <Breakdown title="Calls by Type" items={stats.breakdown} total={stats.total} />
            {hasOutcomes ? <Breakdown title="Calls by Outcome" items={stats.byOutcome} total={stats.total} /> : null}
        </div>
    );
};

export default CallLogsSummary;
